import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import LabValues from "./LabValues";
import InitContext from "../pages/InitContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faChartLine,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

import useMmcService from "../../services/MmcService";
import ErrorMessage from "../errorMessage/ErrorMessage";
import Spinner from "../spinner/Spinner";
import LabTestsLineChart from "./LabTestsLineChart";
import { formatDateTimeForGui, parseDate } from "../pages/utils";
import ReactDOM from "react-dom";
import {Consts} from "../pages/consts";
import SortAndAddNewTest from "./SortAndAddNewTest";

const LabTestsTable = ({
  children,
  selectedPatient,
  hcpView,
  printView = false,
}) => {
  const { t: t } = useTranslation();

  const { loading, error, clearError, getDbLabTests, deleteDbLabTest } =
    useMmcService();

  const [labTests, setLabTests] = useState([]);
  const [addLabTest, setAddLabTest] = useState(false);
  const context = useContext(InitContext);
  const [showLabTestsLineChart, setShowLabTestsLineChart] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    key: Consts.TEST_DATE,
    ascending: false,
  });

  const [sortedLabTests, setSortedLabTests] = useState(
    labTests ? [...labTests] : [],
  );

  useEffect(() => {
    const sorted = sortData(sortConfig?.key, labTests, sortConfig?.ascending);
    setSortedLabTests(sorted);
  }, [labTests, sortConfig]);

  const sortData = (key, labTests, ascending) => {
    if (!labTests || labTests?.length === 0) return [];
    return [...labTests]?.sort((a, b) => {
      const valueA = a[key];
      const valueB = b[key];

      if (valueA === null && valueB === null) {
        return 0;
      }

      if (valueA === null) {
        return ascending ? -1 : 1;
      }

      if (valueB === null) {
        return ascending ? 1 : -1;
      }

      const dateA = parseDate(valueA);
      const dateB = parseDate(valueB);

      if (dateA || dateB) {
        return ascending ? (dateA > dateB ? 1 : -1) : dateA < dateB ? 1 : -1;
      }

      return ascending ? (valueA > valueB ? 1 : -1) : valueA < valueB ? 1 : -1;
    });
  };

  const handleSortChange = (key) => {
    const ascending = sortConfig?.key === key ? !sortConfig?.ascending : true;
    setSortConfig({ key, ascending });
  };

  const patientId = !selectedPatient ? context?.patient?.id : selectedPatient?.id;

   useEffect(() => {
    clearError();

    if (patientId) {
      getDbLabTests(context?.token, patientId).then((data) => {
        handleLabTestsFromDb(data);
      });
    }
  }, [printView, patientId]);

  const handleLabTestsFromDb = (data) => {
    const newArr = data?.filter((test) => test?.labParams?.length > 0);

    if (newArr?.length > 0) {
      setLabTests(
        newArr?.map((test) => ({
          ...test,
          testDate: test?.testDate?.split(".")[0],
        })),
      );
    }
  };

  const handleCloseLabTestsLineChart = () => {
    setShowLabTestsLineChart(false);
  };

  let message =
    error && error?.includes(Consts.DELETE)
      ? t("error_by_deleting_a_lab_test")
      : t("error_by_retrieving_lab_tests");

  const errorMessage = error && <ErrorMessage message={message} />;
  const spinner = !printView && loading && <Spinner />;

  const handleDeleteLabTest = (testToDelete) => {
    clearError();
    if (testToDelete) {
      deleteDbLabTest(context?.token, testToDelete?.id).then((deletedTest) =>
        handleDeleteResult(deletedTest),
      );
    }
  };

  const handleDeleteResult = (deletedTest) => {
    if (deletedTest) {
      setLabTests(
        labTests?.filter((test) => {
          if (!test?.id || (test?.id && test?.id !== deletedTest?.id)) {
            return test;
          }
        }),
      );
    }
  };

  const handleSaveNewLabTest = (labTest) => {
    if (labTest) {
      labTest = {
        ...labTest,
        testDate: formatDateTimeForGui(labTest?.testDate),
      };

      setLabTests([...labTests, labTest]);
    }

    setAddLabTest(false);
  };

  const buildLabParamsView = (params, testName) => {
    return params?.map((param, k) => {
      const parts = param?.parameterName.split("&&");

      return (
        <div key={k}>
          {testName === "plasma_concentration_test" ? (
            <>
              <label className="bold-label">
                {parts[0].split("##")[0]}:{" "}
              </label>
              <label className="label-margin-left">
                {param?.value} {param?.unit}
              </label>
              <label className="label-margin-left">
                ({parts[1] !== "" && `${t("hours_before_test")}: ${parts[1]} ${t("hours_low")}`})
              </label>
            </>
          ) : (
            <>
              <label className="bold-label">
                {t(parts[0])}:{" "}
              </label>
              <label className="label-margin-left">
                {param?.value}{" "}
                {param?.unit}
              </label>
            </>
          )}
        </div>
      );
    })
  }

  return (
    <>
      {addLabTest &&
        ReactDOM.createPortal(
          <LabValues
            selectedPatient={selectedPatient}
            onSaveNewLabTest={handleSaveNewLabTest}
            showDialog={addLabTest}
            hcpView={hcpView}
          />,
          document.getElementById(Consts.ROOT),
        )
      }
      {showLabTestsLineChart &&
        ReactDOM.createPortal(
          <LabTestsLineChart
            labTests={labTests}
            onCloseDialog={handleCloseLabTestsLineChart}
            showDialog={showLabTestsLineChart}
            patientId={patientId}
          />,
          document.getElementById(Consts.ROOT),
        )
      }
      {!printView && (
        <>
          <header className="w3-container w3-center w3-padding-small">
            <div className="w3-display-container w3-padding-small">
              {children}
              {labTests?.length > 0 && (
                <div className="w3-display-right">
                  <button
                    className="w3-btn w3-round-xlarge w3-theme-d5"
                    onClick={() => setShowLabTestsLineChart(true)}
                  >
                    <FontAwesomeIcon icon={faChartLine} />
                  </button>
                </div>
              )}
            </div>
          </header>
          {errorMessage}
          {spinner}
          <SortAndAddNewTest
            labTests={sortedLabTests}
            handleSortChange={handleSortChange}
            setAddLabTest={setAddLabTest}
            sortConfig={sortConfig}
          />
        </>
      )}
      <div className={ printView ? "w3-container" : "w3-container form-content-scroll"}>
        {sortedLabTests?.length > 0 ? (
          <div className="w3-container">
            {sortedLabTests?.map((item, i) => {
              return (
                <div
                  className="w3-row w3-container w3-padding-small w3-margin w3-border w3-border-blue w3-round-large"
                  key={`paramItem${i}`}
                >
                  <div className="w3-threequarter">
                    <div className="w3-col s10 w3-mobile">
                      <label>{item?.testDate}: {t(item?.labTestName)}</label>
                      {buildLabParamsView(item?.labParams, item?.labTestName)}
                    </div>
                  </div>
                  {!printView && (
                    <div className="w3-quarter">
                      <label
                        onClick={() => handleDeleteLabTest(item)}
                        className="w3-btn w3-round-xlarge w3-theme-d5 w3-right"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </label>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="w3-container w3-center">
            <label>
              {selectedPatient ? t("not_available") : t("you_have_not_yet_entered_any_lab_test_results")}.
            </label>
          </div>
        )}
      </div>
    </>
  );
};

export default LabTestsTable;
